import { useQuery, useQueryClient } from "@tanstack/react-query";
import { boronClient } from "../../api";
import { createError, HTTPErrors } from "../../errors";
import { StudyRecordWithRestrictedAttributes } from "../../interfaces/StudyRecordInterface";
import { paths } from "../../lib/api/v1";

// サマリータブの生徒単位・指定日の学習記録を取得するフック（boron WEB-1706）。
// ページングなしで当日全件（recorded_at 降順）が返る。レスポンスに生徒メタは含まれない。
// NOTE: 生徒パス（/students/{id}/...）はリアルタイム/生徒タイムラインが使う校舎パス
// （/sections/{id}/...・hooks/http/useFetchTimelineStudyRecords）とは別物。サマリー専用のため
// feature 内に置く。旧 timeline/daily と異なり、生徒一覧（useFetchTimelineStudents）と分離して
// 生徒ごとに取得する
const URL = "/api/v1/students/{student_id}/timeline/study_records";
type Url = typeof URL;

type TimelineStudyRecordsResponse =
  paths[Url]["get"]["responses"]["200"]["content"]["application/json"];
export type TimelineStudyRecord =
  TimelineStudyRecordsResponse["studyRecords"][0];

type Props = {
  studentId: string;
  // 表示対象の日付（yyyy-MM-dd）。API 必須（欠落・不正は 400）
  date: string;
  enabled: boolean;
};

export const useFetchStudentDailyStudyRecords = ({
  studentId,
  date,
  enabled,
}: Props) => {
  const queryKey = [URL, studentId, date];
  const { data, ...result } = useQuery<
    TimelineStudyRecordsResponse,
    HTTPErrors
  >({
    queryKey,
    queryFn: async () => {
      const { data, response } = await boronClient.GET(URL, {
        params: {
          path: { student_id: studentId },
          query: { date },
        },
      });

      if (response.ok && data) {
        return data;
      }

      throw await createError(response);
    },
    enabled,
  });

  const queryClient = useQueryClient();

  // いいねやコメントをした学習記録をキャッシュに反映する(画面に反映する)ための関数
  const updateStudyRecordQueryCache = (
    newStudyRecord: StudyRecordWithRestrictedAttributes,
  ) => {
    queryClient.setQueriesData<TimelineStudyRecordsResponse>(
      { queryKey },
      (oldData) => {
        if (!oldData) {
          return oldData;
        }

        return {
          studyRecords: oldData.studyRecords.map((oldStudyRecord) =>
            oldStudyRecord.id === newStudyRecord.id
              ? { ...oldStudyRecord, ...newStudyRecord }
              : oldStudyRecord,
          ),
        };
      },
    );
  };

  return {
    studyRecords: data?.studyRecords,
    updateStudyRecordQueryCache,
    ...result,
  };
};
